import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  FlatList,
  Pressable,
  Platform,
  ActivityIndicator,
} from "react-native";
import {
  ChevronDown,
  MapPin,
  TrendingUp,
  Activity,
  ArrowDownAZ,
  LayoutGrid,
  Heart,
  FlaskConical,
  Syringe,
  Dna,
  Users,
  Snowflake,
  Globe,
  Sparkles,
  type LucideIcon,
} from "lucide-react-native";
import {
  EggFreezingIcon,
  SpermFreezingIcon,
} from "../../components/icons/cryoFilterIcons";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  useOrganizations,
  getInsuranceMatch,
  ORGANIZATION_COUNTRY_OPTIONS,
  getOrganizationCountryLabel,
  type SortMode,
} from "@louise/providers/hooks/useOrganizations";
import { useProviderFilters } from "@louise/providers/hooks/useProviderFilters";
import { useUserLocation } from "@louise/clinics/hooks/useUserLocation";
import {
  calculateDistance,
  formatDistanceForCountry,
} from "@louise/providers/utils/distance";
import type {
  Organization,
  ProviderFilters,
} from "@louise/providers/providers.types";
import { FINDCARE_TYPES } from "@louise/providers/providers.types";
import { useSavedClinics } from "@louise/providers/hooks/useSavedClinics";
import { EMBER } from "../../../onboarding/theme";
import {
  applyPatientDefaultIfUnselected,
  FIND_CARE_TREATMENT_NAMES,
  getInitialFindCareFilter,
  normalizePlannedModalities,
  getTreatmentIntentForFilter,
  resolvePatientDefaultFilter,
  type FindCareFilterId,
} from "./findCareIntent";
import type { AccessStackParamList } from "../../types";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  AnchoredSelectMenu,
  MoonFilterChip,
  MoonSearchInput,
  BackLink,
  ClinicalDataStatus,
  LoadingState,
  MoonSkeleton,
} from "@louise/moon-ui";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import MoonClinicCard from "../../components/MoonClinicCard";
import MoonClinicsFilters from "../../components/MoonClinicsFilters";
import { usePlacesPredictions } from "@src/packages/hooks/usePlacesPredictions";
import {
  getPlaceDetails,
  extractPlaceArea,
} from "@src/packages/clinics/utils/googlePlaces";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import { useCoverage } from "@louise/coverage/hooks/useCoverage";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import { useTranslations } from "@src/packages/i18n";
import type { TranslationFunction } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors, semanticColors } from "@louise/theme/tokens";

function getQuickFilters(t: TranslationFunction): {
  id: FindCareFilterId;
  label: string;
  icon: LucideIcon;
}[] {
  return [
    {
      id: "All",
      label: t("access.find_care.quick_filter.all"),
      icon: LayoutGrid,
    },
    {
      id: "Saved",
      label: t("access.find_care.quick_filter.saved"),
      icon: Heart,
    },
    {
      id: "IVF",
      label: t("access.find_care.quick_filter.ivf"),
      icon: FlaskConical,
    },
    {
      id: "FET",
      label: t("access.find_care.quick_filter.fet"),
      icon: Snowflake,
    },
    {
      id: "Embryo Freezing",
      label: t("access.find_care.quick_filter.embryo_freezing"),
      icon: Snowflake,
    },
    {
      id: "Egg Freezing",
      label: t("access.find_care.quick_filter.egg_freezing"),
      icon: EggFreezingIcon,
    },
    {
      id: "Sperm Freezing",
      label: t("access.find_care.quick_filter.sperm_freezing"),
      icon: SpermFreezingIcon,
    },
    {
      id: "IUI",
      label: t("access.find_care.quick_filter.iui"),
      icon: Syringe,
    },
    {
      id: "PGT",
      label: t("access.find_care.quick_filter.pgt"),
      icon: Dna,
    },
    {
      id: "Donor",
      label: t("access.find_care.quick_filter.donor"),
      icon: Users,
    },
  ];
}

type SectorValue = "all" | "public" | "private";

type CountryOption = (typeof ORGANIZATION_COUNTRY_OPTIONS)[number];

const CLINIC_CARD_GAP = 12;

function normalizeHomeCountry(patientCountry: string | undefined | null) {
  const rawCountry = patientCountry?.trim();
  if (!rawCountry) return "US";
  const alias = rawCountry.toUpperCase();
  const normalized =
    {
      CANADA: "CA",
      FRANCE: "FR",
      MEXICO: "MX",
      USA: "US",
      "UNITED STATES": "US",
      "UNITED STATES OF AMERICA": "US",
    }[alias] ?? alias;
  if (/^[A-Z]{2}$/.test(normalized)) return normalized;
  return "US";
}

function countryLabel(country: string) {
  return getOrganizationCountryLabel(country);
}

function getInternationalOptions(homeCountry: string) {
  return ORGANIZATION_COUNTRY_OPTIONS.filter(
    (option) => option.value !== homeCountry,
  );
}

function filterInternationalCountries(
  current: string[],
  options: CountryOption[],
) {
  return current.filter((country) =>
    options.some((option) => option.value === country),
  );
}

function ensureInternationalCountries(
  current: string[],
  options: CountryOption[],
) {
  const valid = filterInternationalCountries(current, options);
  return valid.length > 0 ? valid : options.map((option) => option.value);
}

function getAgeBandFromDateOfBirth(dateOfBirth?: string | null) {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  if (age < 35) return "<35";
  if (age <= 37) return "35-37";
  if (age <= 40) return "38-40";
  return ">40";
}

function pickDesiredTreatments(activeFilter: FindCareFilterId | null) {
  const treatmentIntent = getTreatmentIntentForFilter(activeFilter);
  return treatmentIntent ? [FIND_CARE_TREATMENT_NAMES[treatmentIntent]] : [];
}

function getSortOptions(
  t: TranslationFunction,
  hasLocation: boolean,
): { mode: SortMode; label: string; icon: LucideIcon }[] {
  return [
    {
      mode: "best-match",
      label: t("access.find_care.sort.best_match"),
      icon: Sparkles,
    },
    // "Nearest" needs the user's coordinates; without them it produces a
    // meaningless order, so only offer it when a location is available.
    ...(hasLocation
      ? [
          {
            mode: "nearest" as SortMode,
            label: t("access.find_care.sort.nearest"),
            icon: MapPin,
          },
        ]
      : []),
    {
      mode: "success-rate",
      label: t("access.find_care.sort.success_rate"),
      icon: TrendingUp,
    },
    {
      mode: "most-experience",
      label: t("access.find_care.sort.most_experience"),
      icon: Activity,
    },
    { mode: "name", label: t("access.find_care.sort.name"), icon: ArrowDownAZ },
  ];
}

type Props = NativeStackScreenProps<AccessStackParamList, "FindCare">;

type SearchCenter = { latitude: number; longitude: number; label: string };

// Dev-only fixture (`?dev_findcare_center=boston`) that pre-sets the search
// center on mount so the re-centered state renders deterministically for visual
// evidence, without a live Places call (CI has no Maps key).
const DEV_FINDCARE_CENTERS: Record<string, SearchCenter> = {
  boston: { latitude: 42.3601, longitude: -71.0589, label: "Boston, MA" },
  "new york": { latitude: 40.7128, longitude: -74.006, label: "New York, NY" },
  chicago: { latitude: 41.8781, longitude: -87.6298, label: "Chicago, IL" },
};

function getDevFindCareCenter(): SearchCenter | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const key = new URLSearchParams(window.location.search)
      .get("dev_findcare_center")
      ?.trim()
      .toLowerCase();
    return key ? (DEV_FINDCARE_CENTERS[key] ?? null) : null;
  } catch {
    return null;
  }
}

export default function FindCareScreen({ navigation, route }: Props) {
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const t = useTranslations();
  const capturePress = usePressCapture("FindCareScreen");
  const backLabel = getBackLabel(returnTab, "Access", t("you.shared.back"));
  const { location: userLocation } = useUserLocation();
  // Which point the provider list is measured and sorted from. Defaults to the
  // person's own location; searching a city re-centers here without changing
  // any other filter.
  const [searchCenter, setSearchCenter] = useState<SearchCenter | null>(() =>
    getDevFindCareCenter(),
  );
  // Whether the shared search field is focused: while it is, its text also
  // drives Places city suggestions (dual purpose — name filter + place picker).
  const [searchFocused, setSearchFocused] = useState(false);
  const centerCoords = useMemo(
    () =>
      searchCenter
        ? { latitude: searchCenter.latitude, longitude: searchCenter.longitude }
        : userLocation
          ? {
              latitude: userLocation.latitude,
              longitude: userLocation.longitude,
            }
          : null,
    [searchCenter, userLocation],
  );
  const locationBias = useMemo(
    () =>
      userLocation
        ? { latitude: userLocation.latitude, longitude: userLocation.longitude }
        : null,
    [userLocation],
  );
  const { data: patient, isLoading: isPatientLoading } = usePatient();
  const { canonical, status: experienceStatus } =
    useAuthorizedExperienceProjection();
  const { primaryPolicy } = useCoverage();
  const quickFilters = useMemo(() => getQuickFilters(t), [t]);
  const sortOptions = useMemo(
    () => getSortOptions(t, !!centerCoords),
    [t, centerCoords],
  );
  const routeIntent = route.params?.filter;
  const patientContext = useMemo(
    () => ({
      gender: patient?.gender,
      treatmentGoal: canonical?.intent.goal,
      primaryIntent: canonical?.intent.primaryIntent,
      plannedModalities: normalizePlannedModalities(
        canonical?.intent.plannedModalities,
      ),
      acceptedTreatmentFamily:
        canonical?.acceptedTreatmentPlan?.treatmentFamily,
    }),
    [canonical, patient?.gender],
  );
  const [activeFilter, setActiveFilter] = useState<FindCareFilterId | null>(
    () =>
      getInitialFindCareFilter({
        routeIntent,
        patient: patientContext,
        isPatientLoading,
      }),
  );
  const hasManualFilterSelection = useRef(false);
  const patientDefaultFilter = useMemo(
    () =>
      resolvePatientDefaultFilter({
        ...patientContext,
        isLoading: isPatientLoading || experienceStatus === "loading",
      }),
    [experienceStatus, isPatientLoading, patientContext],
  );
  const isResolvingPatientDefault = activeFilter === null;
  const homeCountry = useMemo(
    () => normalizeHomeCountry(patient?.addresses?.[0]?.country),
    [patient?.addresses],
  );
  const internationalOptions = useMemo(
    () => getInternationalOptions(homeCountry),
    [homeCountry],
  );
  const [includeHomeCountry, setIncludeHomeCountry] = useState(true);
  const [includeInternational, setIncludeInternational] = useState(false);
  const [selectedInternationalCountries, setSelectedInternationalCountries] =
    useState<string[]>(() =>
      getInternationalOptions(
        normalizeHomeCountry(patient?.addresses?.[0]?.country),
      ).map((option) => option.value),
    );
  const [activeSector, setActiveSector] = useState<SectorValue>("all");
  const showSectorFilter = homeCountry === "FR";
  const [sortMode, setSortMode] = useState<SortMode>("best-match");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortTriggerRef = useRef<View>(null);
  const { filters, filtersWithDebouncedSearch, updateFilters, replaceFilters } =
    useProviderFilters();
  // Dual-purpose search: the same text also drives Places city suggestions
  // (biased to the person's location) while the field is focused. Picking one
  // re-centers the list; it does not change the name/city/state text filter.
  const searchQuery = filters.search ?? "";
  const { predictions: locationPredictions, isSearching: isSearchingLocation } =
    usePlacesPredictions({
      query: searchQuery,
      minChars: 3,
      debounceMs: 300,
      regionsOnly: true,
      biasLocation: locationBias,
      enabled: searchFocused,
    });
  const [resolvingPlaceId, setResolvingPlaceId] = useState<string | null>(null);
  const handleLocationPick = useCallback(
    async (placeId: string) => {
      if (resolvingPlaceId) return;
      setResolvingPlaceId(placeId);
      const details = await getPlaceDetails(placeId);
      const area = details ? extractPlaceArea(details) : null;
      setResolvingPlaceId(null);
      if (area && area.latitude != null && area.longitude != null) {
        setSearchCenter({
          latitude: area.latitude,
          longitude: area.longitude,
          label: area.label,
        });
        // Clear the text filter so the picked place drives distance/sort,
        // not a name/city substring match, and dismiss the suggestions.
        updateFilters({ search: "" });
        setSearchFocused(false);
      }
    },
    [resolvingPlaceId, updateFilters],
  );
  const { savedIds, isSaved, hasNotes, toggleSave } = useSavedClinics();
  const userCarrier =
    primaryPolicy?.insuranceCarrier ?? filtersWithDebouncedSearch.carriers?.[0];
  const patientAgeBand = useMemo(
    () => getAgeBandFromDateOfBirth(patient?.dateOfBirth),
    [patient?.dateOfBirth],
  );
  const desiredTreatments = useMemo(
    () => pickDesiredTreatments(activeFilter),
    [activeFilter],
  );
  const activeTreatmentIntent = getTreatmentIntentForFilter(activeFilter);
  const decisionContext = useMemo(
    () => ({
      ageBand: patientAgeBand,
      desiredTreatments,
      userInsuranceCarrier: userCarrier,
    }),
    [desiredTreatments, patientAgeBand, userCarrier],
  );

  useEffect(() => {
    setSelectedInternationalCountries((current) =>
      ensureInternationalCountries(current, internationalOptions),
    );
  }, [internationalOptions]);

  useEffect(() => {
    setActiveFilter((currentFilter) =>
      applyPatientDefaultIfUnselected({
        currentFilter,
        hasManualSelection: hasManualFilterSelection.current,
        patientDefault: patientDefaultFilter,
      }),
    );
  }, [patientDefaultFilter]);

  const internationalCountries = useMemo(
    () => (includeInternational ? selectedInternationalCountries : []),
    [includeInternational, selectedInternationalCountries],
  );

  const baseFilters: ProviderFilters = useMemo(
    () => ({
      ...filtersWithDebouncedSearch,
      type: FINDCARE_TYPES,
      ...(centerCoords
        ? {
            userLocation: {
              lat: centerCoords.latitude,
              lng: centerCoords.longitude,
            },
          }
        : {}),
      ...(userCarrier
        ? {
            userInsuranceCarrier: userCarrier,
          }
        : {}),
      ...(activeTreatmentIntent
        ? { treatments: [FIND_CARE_TREATMENT_NAMES[activeTreatmentIntent]] }
        : {}),
    }),
    [
      filtersWithDebouncedSearch,
      centerCoords,
      userCarrier,
      activeTreatmentIntent,
    ],
  );

  const sectorSpecialties = useMemo(
    () => (activeSector === "all" ? [] : [`sector:${activeSector}`]),
    [activeSector],
  );

  const domesticFilters: ProviderFilters = useMemo(
    () => ({
      ...baseFilters,
      country: includeHomeCountry ? [homeCountry] : ["__none__"],
      ...(sectorSpecialties.length > 0
        ? { specialties: sectorSpecialties }
        : {}),
    }),
    [baseFilters, homeCountry, includeHomeCountry, sectorSpecialties],
  );

  const internationalFilters: ProviderFilters = useMemo(
    () => ({
      ...baseFilters,
      country:
        includeInternational && internationalCountries.length > 0
          ? internationalCountries
          : ["__none__"],
    }),
    [baseFilters, includeInternational, internationalCountries],
  );

  const {
    data: domesticData,
    totalFiltered: totalDomestic,
    hasNextPage: hasNextDomesticPage,
    fetchNextPage: fetchNextDomesticPage,
    isLoading: isLoadingDomestic,
    error: domesticLoadError,
    retry: retryDomestic,
    isRetrying: isRetryingDomestic,
  } = useOrganizations(domesticFilters, sortMode, decisionContext);
  const {
    data: internationalData,
    totalFiltered: totalInternational,
    hasNextPage: hasNextInternationalPage,
    fetchNextPage: fetchNextInternationalPage,
    isLoading: isLoadingInternational,
    error: internationalLoadError,
    retry: retryInternational,
    isRetrying: isRetryingInternational,
  } = useOrganizations(internationalFilters, sortMode, decisionContext);
  const organizationsLoadFailed =
    !!domesticLoadError || (includeInternational && !!internationalLoadError);
  const retryOrganizations = useCallback(() => {
    if (domesticLoadError) retryDomestic();
    if (internationalLoadError) retryInternational();
  }, [
    domesticLoadError,
    internationalLoadError,
    retryDomestic,
    retryInternational,
  ]);

  // Apply "Saved" filter client-side since saved IDs live in context
  const organizations =
    activeFilter === "Saved"
      ? domesticData.filter((org) => savedIds.has(org.id))
      : domesticData;
  const internationalOrganizations =
    activeFilter === "Saved"
      ? internationalData.filter((org) => savedIds.has(org.id))
      : internationalData;
  const totalFiltered =
    activeFilter === "Saved"
      ? organizations.length + internationalOrganizations.length
      : totalDomestic + (includeInternational ? totalInternational : 0);
  const isLoadingOrganizations =
    isResolvingPatientDefault ||
    isLoadingDomestic ||
    (includeInternational && isLoadingInternational);

  // Pre-compute distances for display
  const distanceMap = useMemo(() => {
    if (!centerCoords) return new Map<string, string>();
    const map = new Map<string, string>();
    for (const org of [...organizations, ...internationalOrganizations]) {
      if (org.location) {
        const dist = calculateDistance(
          centerCoords.latitude,
          centerCoords.longitude,
          org.location.lat,
          org.location.lng,
        );
        map.set(org.id, formatDistanceForCountry(dist, org.address.country));
      }
    }
    return map;
  }, [organizations, internationalOrganizations, centerCoords]);

  const onOrgPress = useCallback(
    (org: Organization) => {
      navigation.navigate("ClinicDetail", { clinicId: org.id });
    },
    [navigation],
  );

  const handleFilterPress = useCallback((id: FindCareFilterId) => {
    hasManualFilterSelection.current = true;
    setActiveFilter(id);
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters: typeof filters) => {
      replaceFilters(newFilters);
      hasManualFilterSelection.current = true;
      setActiveFilter("All");
    },
    [replaceFilters],
  );

  const handleLocationScopeChange = useCallback(
    (next: {
      includeHomeCountry: boolean;
      includeInternational: boolean;
      internationalCountries: string[];
    }) => {
      setIncludeHomeCountry(next.includeHomeCountry);
      setIncludeInternational(next.includeInternational);
      setSelectedInternationalCountries(
        filterInternationalCountries(
          next.internationalCountries,
          internationalOptions,
        ),
      );
    },
    [internationalOptions],
  );

  const renderCard = useCallback(
    (item: Organization) => {
      return (
        <View
          key={item.id}
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <MoonClinicCard
            organization={item}
            onPress={onOrgPress}
            distance={distanceMap.get(item.id) ?? null}
            insuranceMatch={getInsuranceMatch(item, userCarrier)}
            decisionContext={decisionContext}
            isSaved={isSaved(item.id)}
            hasNotes={hasNotes(item.id)}
            onToggleSave={toggleSave}
            homeCountry={homeCountry}
          />
        </View>
      );
    },
    [
      onOrgPress,
      distanceMap,
      userCarrier,
      decisionContext,
      isSaved,
      hasNotes,
      toggleSave,
      homeCountry,
    ],
  );

  const renderOrg = useCallback(
    ({ item }: { item: Organization }) => renderCard(item),
    [renderCard],
  );

  const handleEndReached = useCallback(() => {
    if (hasNextDomesticPage) {
      fetchNextDomesticPage();
      return;
    }
    if (includeInternational && hasNextInternationalPage) {
      fetchNextInternationalPage();
    }
  }, [
    hasNextDomesticPage,
    fetchNextDomesticPage,
    includeInternational,
    hasNextInternationalPage,
    fetchNextInternationalPage,
  ]);

  const activeSortLabel =
    sortOptions.find((s) => s.mode === sortMode)?.label ??
    t("access.find_care.sort.nearest");

  const locationChipLabel = searchCenter
    ? t("access.find_care.location.near", {
        params: { place: searchCenter.label },
      })
    : "";
  const showLocationSuggestions =
    searchFocused &&
    searchQuery.trim().length >= 3 &&
    (locationPredictions.length > 0 || isSearchingLocation);
  const displayedTotalFiltered = isResolvingPatientDefault ? 0 : totalFiltered;

  const ListHeader = (
    <View
      style={{
        paddingHorizontal: 16,
        paddingTop: SCREEN_PADDING_TOP,
        // Lift the header (and the location dropdown that overflows from it)
        // above the provider cards, which render after it in the list.
        position: "relative",
        zIndex: 10,
      }}
    >
      {/* Back button */}
      <BackLink
        label={returnTab ? backLabel : t("access.find_care.back")}
        onPress={handleBack}
        style={{ marginBottom: 8 }}
      />

      {/* Title */}
      <Text
        className="font-dm-sans"
        style={
          {
            fontSize: 38,
            fontWeight: "700",
            color: colors.deepBrown,
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          } as Record<string, unknown>
        }
      >
        {t("access.find_care.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          color: EMBER.colors.textLight,
          marginTop: 2,
          marginBottom: 18,
        }}
      >
        {t("access.find_care.providers_count", {
          params: { count: String(displayedTotalFiltered) },
        })}
      </Text>

      {/* Search bar */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <MoonSearchInput
          placeholder={t("access.find_care.search_placeholder")}
          value={filters.search ?? ""}
          onChangeText={(text) => updateFilters({ search: text })}
          onClear={() => updateFilters({ search: "" })}
          onFocus={() => setSearchFocused(true)}
          // Delay so a tap on a suggestion row lands before the list hides.
          onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
          tone="glass"
          style={{ flex: 1 }}
        />
        <MoonClinicsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          homeCountry={homeCountry}
          includeHomeCountry={includeHomeCountry}
          includeInternational={includeInternational}
          internationalCountries={selectedInternationalCountries}
          internationalOptions={internationalOptions}
          onLocationScopeChange={handleLocationScopeChange}
        />
      </View>

      {/* Location suggestions: while the search field is focused its text also
          offers cities to re-center on. Rendered inline (not an overlay) so it
          flows in the header without stacking-context fights inside the list. */}
      {showLocationSuggestions ? (
        <View
          style={{
            marginTop: 8,
            borderRadius: 14,
            backgroundColor: semanticColors.surface.input,
            borderWidth: 1,
            borderColor: semanticColors.border.subtle,
            overflow: "hidden",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              paddingHorizontal: 14,
              paddingTop: 10,
              paddingBottom: 4,
              fontSize: 11,
              fontWeight: "700",
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: EMBER.colors.textFaint,
            }}
          >
            {t("access.find_care.location.suggestions_label")}
          </Text>
          {locationPredictions.slice(0, 4).map((prediction, index) => (
            <Pressable
              key={prediction.placeId}
              onPress={() => {
                capturePress(
                  pressLabel(prediction.structuredFormatting.mainText),
                  "location_suggestion_row",
                );
                handleLocationPick(prediction.placeId);
              }}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 10,
                paddingHorizontal: 14,
                paddingVertical: 11,
                borderTopWidth: index > 0 ? 1 : 0,
                borderTopColor: semanticColors.border.subtle,
              }}
            >
              <MapPin
                size={14}
                color={EMBER.colors.textFaint}
                strokeWidth={1.6}
              />
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  numberOfLines={1}
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {prediction.structuredFormatting.mainText}
                </Text>
                {prediction.structuredFormatting.secondaryText ? (
                  <Text
                    className="font-dm-sans"
                    numberOfLines={1}
                    style={{
                      fontSize: 12,
                      color: EMBER.colors.textLight,
                      marginTop: 1,
                    }}
                  >
                    {prediction.structuredFormatting.secondaryText}
                  </Text>
                ) : null}
              </View>
              {resolvingPlaceId === prediction.placeId ? (
                <ActivityIndicator
                  size="small"
                  color={EMBER.colors.textLight}
                />
              ) : null}
            </Pressable>
          ))}
        </View>
      ) : null}

      {/* Quick Filter Chips + Sort. An active searched location shows here as a
          removable chip, pinned before the scrolling quick filters. */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingVertical: 14,
        }}
      >
        {searchCenter ? (
          <MoonFilterChip
            label={locationChipLabel}
            icon={MapPin}
            selected
            showCheck={false}
            tone="glass"
            onPress={() => setSearchCenter(null)}
            onRemove={() => setSearchCenter(null)}
            removeAccessibilityLabel={t("access.find_care.location.clear")}
            accessibilityLabel={locationChipLabel}
          />
        ) : null}
        <FlatList
          horizontal
          data={quickFilters}
          keyExtractor={(item) => item.id}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 8 }}
          style={{ flex: 1 }}
          renderItem={({ item: { id, label, icon } }) => (
            <MoonFilterChip
              label={label}
              icon={icon}
              selected={activeFilter === id}
              onPress={() => handleFilterPress(id)}
              tone="glass"
              showCheck={false}
            />
          )}
        />
      </View>

      {/* Sector filter — FR only */}
      {showSectorFilter && (
        <View
          style={{
            flexDirection: "row",
            gap: 8,
            marginBottom: 8,
          }}
        >
          {(
            [
              { value: "all", label: t("access.find_care.sector.all") },
              {
                value: "public",
                label: `🏥 ${t("access.find_care.sector.public")}`,
              },
              {
                value: "private",
                label: `🏛️ ${t("access.find_care.sector.private")}`,
              },
            ] as const
          ).map((sector) => (
            <MoonFilterChip
              key={sector.value}
              label={sector.label}
              selected={activeSector === sector.value}
              onPress={() => setActiveSector(sector.value)}
              tone="glass"
              showCheck={false}
            />
          ))}
        </View>
      )}

      {/* Sort picker */}
      <View style={{ marginBottom: 8 }}>
        <Pressable
          ref={sortTriggerRef}
          onPress={() => {
            capturePress(
              pressLabel(t("access.find_care.sort.label")),
              "open_sort_menu",
            );
            setShowSortMenu(true);
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            alignSelf: "flex-start",
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
            }}
          >
            {t("access.find_care.sort.label")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "600",
              color: EMBER.colors.textMid,
            }}
          >
            {activeSortLabel}
          </Text>
          <ChevronDown
            size={12}
            color={EMBER.colors.textLight}
            strokeWidth={1.5}
          />
        </Pressable>
      </View>
    </View>
  );

  const emptyMessage = useMemo(() => {
    if (isLoadingOrganizations) return null;
    if (totalFiltered !== 0) return null;
    const isFR = homeCountry === "FR";
    if (activeFilter === "Saved" && isFR) {
      return {
        title: t("access.find_care.empty_saved_fr"),
        hint: t("access.find_care.empty_saved_fr_hint"),
      };
    }
    if (isFR && activeFilter === "PGT") {
      return {
        title: t("access.find_care.empty_pgt_fr"),
        hint: t("access.find_care.empty_pgt_fr_hint"),
      };
    }
    if (isFR && activeFilter === "Donor" && activeSector === "private") {
      return {
        title: t("access.find_care.empty_donor_private_fr"),
        hint: t("access.find_care.empty_donor_private_fr_hint"),
      };
    }
    if (isFR && activeSector !== "all") {
      return {
        title: t("access.find_care.empty_multi_filter_fr"),
        hint: t("access.find_care.empty_multi_filter_fr_hint"),
      };
    }
    return {
      title: t("access.find_care.no_providers_match_search"),
      hint: null,
    };
  }, [
    isLoadingOrganizations,
    totalFiltered,
    homeCountry,
    activeFilter,
    activeSector,
    t,
  ]);

  const ListEmpty = isLoadingOrganizations ? (
    <View
      testID="find-care-loading"
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        rowGap: 12,
        paddingTop: 4,
      }}
    >
      {[0, 1, 2, 3].map((card) => (
        <MoonSkeleton key={card} width="48.5%" height={228} radius={16} />
      ))}
    </View>
  ) : organizationsLoadFailed ? (
    <ClinicalDataStatus
      state="unavailable"
      title={t("access.find_care.couldnt_load_clinics")}
      message={t("states.check_connection_and_try_again")}
      retryLabel={t("clinical_data.retry")}
      retryingLabel={t("clinical_data.retrying")}
      onRetry={retryOrganizations}
      isRetrying={isRetryingDomestic || isRetryingInternational}
      testID="find-care-unavailable"
    />
  ) : emptyMessage ? (
    <SearchEmptyState
      title={emptyMessage.title}
      subtitle={emptyMessage.hint ?? undefined}
      onReset={() => {
        replaceFilters({});
        hasManualFilterSelection.current = true;
        setActiveFilter("All");
        setActiveSector("all");
      }}
    />
  ) : null;

  const showInternationalSection =
    includeInternational &&
    !hasNextDomesticPage &&
    internationalOrganizations.length > 0;
  const internationalSectionCount =
    activeFilter === "Saved"
      ? internationalOrganizations.length
      : totalInternational;

  const ListFooter = (
    <View>
      {hasNextDomesticPage && <LoadingState style={{ paddingVertical: 20 }} />}
      {isLoadingOrganizations && !hasNextDomesticPage && (
        <LoadingState style={{ paddingVertical: 20 }} />
      )}
      {showInternationalSection && (
        <View style={{ paddingTop: 12 }}>
          <View
            style={{
              paddingHorizontal: 16,
              paddingTop: 8,
              paddingBottom: 12,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Globe size={15} color={EMBER.colors.textMid} strokeWidth={1.7} />
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 15,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t("access.find_care.international_section_title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ fontSize: 12, color: EMBER.colors.textLight }}
              >
                {t("access.find_care.international_section_count", {
                  params: {
                    count: String(internationalSectionCount),
                    country: countryLabel(homeCountry),
                  },
                })}
              </Text>
            </View>
          </View>
          {Array.from({
            length: Math.ceil(internationalOrganizations.length / 2),
          }).map((_, rowIndex) => {
            const left = internationalOrganizations[rowIndex * 2];
            const right = internationalOrganizations[rowIndex * 2 + 1];
            if (!left) return null;

            return (
              <View
                key={left.id}
                style={{
                  flexDirection: "row",
                  gap: CLINIC_CARD_GAP,
                  paddingHorizontal: 16,
                  marginBottom: 12,
                }}
              >
                {renderCard(left)}
                {right ? (
                  renderCard(right)
                ) : (
                  <View style={{ flex: 1, minWidth: 0 }} />
                )}
              </View>
            );
          })}
        </View>
      )}
      {!hasNextDomesticPage &&
        includeInternational &&
        hasNextInternationalPage && (
          <LoadingState style={{ paddingVertical: 20 }} />
        )}
    </View>
  );

  return (
    <GlassScreenLayout>
      <FlatList
        key="find-care-two-column"
        numColumns={2}
        data={organizations}
        extraData={distanceMap}
        keyExtractor={(item) => item.id}
        renderItem={renderOrg}
        columnWrapperStyle={{
          gap: CLINIC_CARD_GAP,
          paddingHorizontal: 16,
          marginBottom: 12,
        }}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        ListFooterComponent={ListFooter}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
      />

      {/* Sort menu */}
      <AnchoredSelectMenu
        visible={showSortMenu}
        triggerRef={sortTriggerRef}
        value={sortMode}
        items={sortOptions.map((opt) => ({
          value: opt.mode,
          label: opt.label,
          icon: opt.icon,
          action: `sort_${opt.mode.replace(/-/g, "_")}`,
        }))}
        onSelect={(mode) => {
          const option = sortOptions.find((opt) => opt.mode === mode);
          if (option) setSortMode(option.mode);
          setShowSortMenu(false);
        }}
        onClose={() => {
          // Backdrop dismiss target; it has no visible copy of its own.
          capturePress(pressLabel("dismiss_sort_menu"));
          setShowSortMenu(false);
        }}
        testID="find-care-sort-menu"
      />
    </GlassScreenLayout>
  );
}
